import { useQuery, UseQueryResult } from "@tanstack/react-query"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import { HandledApiError } from "api/queryClient"
import { Availabledocuments } from "api/Repos/documents"
import type { DocumentApiItem } from "types/document.types"
import handleSequenceError from "utils/handleSequenceError"

/**
 * React-query queries for the Documents domain (mutations live in
 * `useDocumentsMutations.ts`).
 */

/**
 * Fetches a project's documents for the given search filter and folder.
 * Filter/folder are part of the key, so navigation and search refetch
 * automatically.
 */
export const useProjectDocumentsQuery = (
  projectId: string,
  owner: string,
  filter: string,
  folder: string,
  enabled: boolean = true
): UseQueryResult<DocumentApiItem[]> => {
  const getAxios = useAuthedAxios()

  return useQuery({
    queryKey: queryKeys.documents.list(projectId, filter, folder),
    enabled: enabled && Boolean(projectId),
    meta: { errorTitle: "Error Fetching Documents!" },
    queryFn: async (): Promise<DocumentApiItem[]> => {
      const res = await Availabledocuments(
        await getAxios(),
        projectId,
        owner,
        filter,
        folder
      )
      if (!res.data) {
        return []
      }
      if (!handleSequenceError(res.data, "Error Retrieving Documents!")) {
        throw new HandledApiError("Sequence error retrieving documents")
      }
      return res.data
    },
  })
}
